import { Box, Group } from "@mantine/core";
import { IconArrowLeft, IconArrowRight } from "@tabler/icons-react";
import { Link } from "gatsby";
import React from "react";

import { Text } from "@components";

interface NavPage {
  id: string;
  title: string;
}

interface DocsPageNavProps {
  currentPageId: string;
  getPrev: (id: string) => NavPage | null;
  getNext: (id: string) => NavPage | null;
  basePath: string;
  homePageId?: string;
}

function NavButton({
  page,
  direction,
  basePath,
  homePageId = "home",
}: {
  page: NavPage;
  direction: "prev" | "next";
  basePath: string;
  homePageId?: string;
}) {
  const href = page.id === homePageId ? `${basePath}/` : `${basePath}/${page.id}/`;

  return (
    <Box
      component={Link}
      to={href}
      style={{
        flex: 1,
        maxWidth: "48%",
        display: "flex",
        flexDirection: "column",
        gap: 4,
        padding: "var(--mantine-spacing-md)",
        background: "var(--mantine-color-layoutDark-5)",
        borderRadius: "var(--mantine-radius-md)",
        textDecoration: "none",
        border: "1px solid transparent",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        textAlign: direction === "next" ? "right" : "left",
      }}
      className="docs-page-nav-btn"
    >
      <Group
        gap={6}
        justify={direction === "next" ? "flex-end" : "flex-start"}
      >
        {direction === "prev" && (
          <IconArrowLeft size={14} color="var(--mantine-color-fontLightSecondary-5)" />
        )}
        <Text
          size="xs"
          c="fontLightSecondary"
          fw={700}
          style={{
            fontSize: 11,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          {direction === "prev" ? "Previous" : "Next"}
        </Text>
        {direction === "next" && (
          <IconArrowRight size={14} color="var(--mantine-color-fontLightSecondary-5)" />
        )}
      </Group>
      <Text
        size="xs"
        c="fontLight"
        fw={600}
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {page.title}
      </Text>
    </Box>
  );
}

export function DocsPageNav({
  currentPageId,
  getPrev,
  getNext,
  basePath,
  homePageId,
}: DocsPageNavProps) {
  const prev = getPrev(currentPageId);
  const next = getNext(currentPageId);

  if (!prev && !next) return null;

  return (
    <Group
      justify="space-between"
      align="stretch"
      mt="xxxl"
      pt="xl"
      style={{
        borderTop: "2px solid var(--mantine-color-decorativeBorder-5)",
      }}
    >
      {prev ? (
        <NavButton page={prev} direction="prev" basePath={basePath} homePageId={homePageId} />
      ) : (
        <div />
      )}
      {next ? (
        <NavButton page={next} direction="next" basePath={basePath} homePageId={homePageId} />
      ) : (
        <div />
      )}
    </Group>
  );
}
